import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Lightbulb, User, Feather, ShieldCheck, Star, Zap, Globe, TrendingUp } from "lucide-react";
import Navbar from "@/components/Navbar";

const SCHEMA_SOFTWARE = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Lakshmi",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "url": "https://hilakshmi.com",
    "description": "AI social media buddy that writes LinkedIn posts, X threads, Instagram captions and Reddit posts in your voice in under 60 seconds. Built for Indian professionals and creators.",
    "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "INR",
        "availability": "https://schema.org/InStock",
        "description": "Free to start — no credit card required"
    },
    "featureList": [
        "LinkedIn post generation",
        "X (Twitter) thread writing",
        "Instagram caption writing",
        "Reddit post writing",
        "Brand voice matching",
        "Multi-platform content",
        "AI quality scoring",
        "Content calendar scheduling"
    ],
    "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "47",
        "bestRating": "5"
    }
};

const SCHEMA_FAQ = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
        {
            "@type": "Question",
            "name": "Is Lakshmi free to use?",
            "acceptedAnswer": { "@type": "Answer", "text": "Yes — Lakshmi is free to start. No credit card required. Sign up and write your first post in under 60 seconds." }
        },
        {
            "@type": "Question",
            "name": "Which platforms does Lakshmi support?",
            "acceptedAnswer": { "@type": "Answer", "text": "Lakshmi writes platform-native posts for LinkedIn, X (Twitter), Instagram, and Reddit — all from one prompt." }
        },
        {
            "@type": "Question",
            "name": "How does Lakshmi learn my writing style?",
            "acceptedAnswer": { "@type": "Answer", "text": "Paste one post you're proud of during onboarding. Lakshmi's Voice Coach agent analyses your rhythm, vocabulary, and tone — and calibrates every future draft to match." }
        },
        {
            "@type": "Question",
            "name": "Is Lakshmi better than ChatGPT for LinkedIn?",
            "acceptedAnswer": { "@type": "Answer", "text": "Yes, for LinkedIn specifically. ChatGPT is a general assistant. Lakshmi is purpose-built for personal brand content — it has a 4-agent pipeline (Strategist, Voice Coach, Ghostwriter, Editor) that produces posts with strong hooks, personal stories, and clear CTAs, not generic AI prose." }
        },
        {
            "@type": "Question",
            "name": "Can I schedule posts with Lakshmi?",
            "acceptedAnswer": { "@type": "Answer", "text": "Yes — Lakshmi has a built-in content calendar where you can plan and schedule posts across platforms." }
        },
        {
            "@type": "Question",
            "name": "Does Lakshmi work for Indian professionals?",
            "acceptedAnswer": { "@type": "Answer", "text": "Lakshmi is built with Indian professionals in mind — founders, sales leaders, engineers, and consultants from Mumbai, Bengaluru, Delhi, Hyderabad and Pune use it to grow their LinkedIn presence." }
        },
        {
            "@type": "Question",
            "name": "How long does it take to generate a post?",
            "acceptedAnswer": { "@type": "Answer", "text": "Under 60 seconds from idea to publishable post. The 4-agent pipeline runs in parallel — strategy, voice matching, writing, and QA all happen automatically." }
        },
        {
            "@type": "Question",
            "name": "What is a brand voice and how does Lakshmi use it?",
            "acceptedAnswer": { "@type": "Answer", "text": "Your brand voice is your unique way of writing — your tone, sentence length, vocabulary, and opinions. Lakshmi learns it from your sample post and stores it. Every post it writes is checked against your voice profile before delivery." }
        }
    ]
};

const SCHEMA_WEBSITE = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Lakshmi",
    "url": "https://hilakshmi.com",
    "description": "AI social media buddy for LinkedIn, X, Instagram and Reddit. Built for Indian professionals.",
    "potentialAction": {
        "@type": "SearchAction",
        "target": {
            "@type": "EntryPoint",
            "urlTemplate": "https://hilakshmi.com/?q={search_term_string}"
        },
        "query-input": "required name=search_term_string"
    }
};

function useJsonLd(schemas) {
    useEffect(() => {
        const tags = schemas.map(schema => {
            const s = document.createElement("script");
            s.type = "application/ld+json";
            s.text = JSON.stringify(schema);
            s.setAttribute("data-schema", schema["@type"]);
            document.head.appendChild(s);
            return s;
        });
        return () => tags.forEach(t => t.remove());
    }, []);
}

const AGENTS = [
    { icon: Lightbulb, title: "Content Strategist", body: "Turns a half-baked thought into a sharp angle. Finds the hook your audience actually stops for.", tag: "Finds the angle", color: "#7C3AED" },
    { icon: User, title: "Voice Coach", body: "Learns how you write — your rhythm, your phrases, your opinions. Every post sounds unmistakably like you.", tag: "Sounds like you", color: "#06B6D4" },
    { icon: Feather, title: "Ghostwriter", body: "Drafts for LinkedIn, X, Instagram and Reddit simultaneously. One idea, four platform-native posts.", tag: "All platforms", color: "#7C3AED" },
    { icon: ShieldCheck, title: "QA Editor", body: "Kills clichés, fixes weak hooks, tightens the CTA — and gives you a quality score before you hit post.", tag: "Ships it clean", color: "#06B6D4" },
];

const TESTIMONIALS = [
    {
        name: "Priya Sharma",
        role: "Startup Founder, Delhi",
        avatar: "PS",
        avatarBg: "#7C3AED",
        text: "I used to spend 2 hours writing one LinkedIn post. Now Lakshmi drafts it in 45 seconds and it sounds more like me than I do. My profile views went up 3x in the first month.",
        stars: 5,
    },
    {
        name: "Arjun Mehta",
        role: "Senior Product Manager, Bengaluru",
        avatar: "AM",
        avatarBg: "#0891B2",
        text: "Tried every AI writing tool out there. This is the first one that doesn't make me cringe. The voice matching is genuinely scary good — my connections thought I hired a writer.",
        stars: 5,
    },
    {
        name: "Neha Agarwal",
        role: "Sales Director, Mumbai",
        avatar: "NA",
        avatarBg: "#059669",
        text: "I post on LinkedIn every day now. Before Lakshmi, I'd go weeks without posting because I didn't know what to write. Now I just type a rough thought and it handles the rest.",
        stars: 5,
    },
    {
        name: "Rohan Kapoor",
        role: "AI Engineer, Hyderabad",
        avatar: "RK",
        avatarBg: "#DC2626",
        text: "The multi-platform feature is a game changer. One prompt → LinkedIn post, X thread, Instagram caption. My entire content calendar done in 10 minutes on Sunday.",
        stars: 5,
    },
];

const PLATFORMS = ["LinkedIn", "X (Twitter)", "Instagram", "Reddit"];

const fadeUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };

function FaqItem({ q, a }) {
    const [open, setOpen] = React.useState(false);
    return (
        <div className="lk-border lk-surface rounded-xl overflow-hidden">
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-[var(--lk-surface-2)] transition-colors"
                aria-expanded={open}
            >
                <span className="font-medium text-sm sm:text-base pr-4">{q}</span>
                <span className="shrink-0 text-[var(--lk-primary)] text-lg leading-none">{open ? "−" : "+"}</span>
            </button>
            {open && (
                <div className="px-6 pb-5 text-sm text-[var(--lk-muted)] leading-relaxed border-t border-[var(--lk-border)] pt-4">
                    {a}
                </div>
            )}
        </div>
    );
}

export default function Landing() {
    useJsonLd([SCHEMA_SOFTWARE, SCHEMA_FAQ, SCHEMA_WEBSITE]);

    return (
        <div className="min-h-screen">
            <Navbar />

            {/* ── Hero ── */}
            <section className="relative overflow-hidden lk-noise">
                {/* Grid background */}
                <div className="absolute inset-0 pointer-events-none" style={{
                    backgroundImage: "linear-gradient(rgba(124,58,237,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(124,58,237,0.04) 1px, transparent 1px)",
                    backgroundSize: "64px 64px",
                }} />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-0 grid md:grid-cols-12 gap-8 items-end">
                    {/* Left copy */}
                    <div className="md:col-span-7 pb-16 sm:pb-20">
                        {/* Badge */}
                        <motion.div {...fadeUp} className="inline-flex items-center gap-2 lk-shimmer border border-[var(--lk-primary)]/30 rounded-full px-4 py-1.5 mb-6">
                            <Sparkles className="w-3.5 h-3.5 text-[var(--lk-primary)]" />
                            <span className="text-xs font-medium text-[var(--lk-primary)]">AI LinkedIn Post Writer &amp; Personal Brand Tool — Now in Beta</span>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            className="font-heading text-4xl sm:text-6xl md:text-[4.5rem] leading-[1.05] tracking-tight"
                        >
                            Your AI buddy for{" "}
                            <span className="lk-gradient-text">building your</span>{" "}
                            personal brand.
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.25 }}
                            className="mt-6 text-base sm:text-lg text-[var(--lk-muted)] max-w-xl leading-relaxed"
                        >
                            Drop a rough idea. Lakshmi — your <span className="text-[var(--lk-text)] font-medium">AI social media manager</span> — writes platform-native posts for{" "}
                            <span className="text-[var(--lk-text)] font-medium">LinkedIn, X, Instagram &amp; Reddit</span>{" "}
                            in your voice, in under 60 seconds. Built for Indian professionals in Bengaluru, Mumbai, Delhi &amp; Hyderabad.
                        </motion.p>

                        {/* Platform pills */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="mt-5 flex flex-wrap gap-2"
                        >
                            {PLATFORMS.map(p => (
                                <span key={p} className="text-xs px-3 py-1 rounded-full border border-[var(--lk-border)] text-[var(--lk-muted)] bg-[var(--lk-surface)]">{p}</span>
                            ))}
                        </motion.div>

                        {/* CTAs */}
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.45 }}
                            className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3"
                        >
                            <Link
                                to="/signup"
                                className="lk-btn-primary rounded-xl px-6 py-3.5 flex items-center gap-2 w-full sm:w-auto justify-center text-sm"
                                data-testid="hero-get-started-btn"
                            >
                                Start for free — no card needed <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/login"
                                className="lk-border rounded-xl px-6 py-3.5 text-sm text-[var(--lk-muted)] hover:text-[var(--lk-text)] hover:bg-[var(--lk-surface-2)] w-full sm:w-auto text-center transition-colors"
                                data-testid="hero-login-link"
                            >
                                I already have an account
                            </Link>
                        </motion.div>

                        {/* Social proof */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            className="mt-6 flex items-center gap-3 flex-wrap"
                        >
                            <div className="flex -space-x-2">
                                {["P","A","R","N","S"].map((initial, i) => (
                                    <div key={i} className="w-7 h-7 rounded-full border-2 border-[var(--lk-bg)] flex items-center justify-center text-[0.6rem] font-bold text-white"
                                        style={{ backgroundColor: ["#7C3AED","#0891B2","#059669","#DC2626","#D97706"][i] }}>
                                        {initial}
                                    </div>
                                ))}
                            </div>
                            <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />)}
                            </div>
                            <span className="text-xs text-[var(--lk-muted)]">200+ creators already building their brand</span>
                        </motion.div>

                        {/* Stats */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.7 }}
                            className="mt-6 flex flex-wrap gap-6"
                        >
                            {[
                                { val: "< 60s", label: "from idea to post" },
                                { val: "4×", label: "platforms at once" },
                                { val: "100%", label: "your voice" },
                            ].map(s => (
                                <div key={s.val}>
                                    <span className="font-mono text-sm font-bold text-[var(--lk-primary)]">{s.val}</span>
                                    <span className="text-xs text-[var(--lk-muted)] ml-1.5">{s.label}</span>
                                </div>
                            ))}
                        </motion.div>
                    </div>

                    {/* Right — Lakshmi character */}
                    <div className="md:col-span-5 flex justify-center md:justify-end">
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="relative w-64 sm:w-72 md:w-full max-w-[320px] lk-float"
                        >
                            {/* Glow */}
                            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-56 h-56 rounded-full blur-3xl pointer-events-none"
                                style={{ background: "radial-gradient(circle, rgba(124,58,237,0.25) 0%, transparent 70%)" }} />
                            {/* Character */}
                            <picture>
                                <source srcSet="/image/lakshmi_nobg.webp" type="image/webp" />
                                <img
                                    src="/image/lakshmi_nobg_opt.png"
                                    alt="Lakshmi AI agent — your social media buddy"
                                    width="560"
                                    height="1120"
                                    className="w-full h-auto block relative z-10"
                                    fetchpriority="high"
                                    style={{
                                        mixBlendMode: "screen",
                                        maskImage: "linear-gradient(to bottom, black 55%, transparent 100%)",
                                        WebkitMaskImage: "linear-gradient(to bottom, black 55%, transparent 100%)",
                                        filter: "drop-shadow(0 0 32px rgba(124,58,237,0.5))",
                                    }}
                                />
                            </picture>
                            {/* Floating badge */}
                            <motion.div
                                initial={{ opacity: 0, x: 16 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.8 }}
                                className="absolute top-6 right-0 bg-[var(--lk-surface-2)]/95 backdrop-blur-sm border border-[var(--lk-primary)]/40 rounded-2xl px-3 py-2.5 shadow-xl"
                            >
                                <div className="flex items-center gap-2">
                                    <Sparkles className="w-3.5 h-3.5 text-[var(--lk-primary)]" />
                                    <span className="text-sm font-semibold">Lakshmi</span>
                                </div>
                                <div className="lk-label mt-0.5">AI Social Buddy</div>
                            </motion.div>
                            {/* Live indicator */}
                            <motion.div
                                initial={{ opacity: 0, x: -16 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 1.0 }}
                                className="absolute bottom-16 left-0 bg-[var(--lk-surface-2)]/95 backdrop-blur-sm border border-[var(--lk-border)] rounded-xl px-3 py-2 shadow-xl flex items-center gap-2"
                            >
                                <div className="w-2 h-2 rounded-full bg-emerald-400 lk-pulse" />
                                <span className="text-xs text-[var(--lk-muted)]">Writing your post...</span>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ── How it works ── */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 border-t border-[var(--lk-border)]">
                <div className="text-center mb-14">
                    <div className="lk-label mb-3">Simple by design</div>
                    <h2 className="font-heading text-3xl sm:text-5xl tracking-tight">Three steps. That's it.</h2>
                    <p className="mt-4 text-[var(--lk-muted)] text-sm sm:text-base max-w-xl mx-auto">
                        No prompt engineering. No template library. No "write me a LinkedIn post about..." just tell Lakshmi what's on your mind.
                    </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 relative">
                    {/* Connector line */}
                    <div className="hidden sm:block absolute top-10 left-1/6 right-1/6 h-px bg-gradient-to-r from-transparent via-[var(--lk-primary)]/30 to-transparent" />
                    {[
                        { n: "01", icon: User, title: "Set your voice", body: "Paste one post you're proud of. That's your voice fingerprint — every future draft is calibrated against it." },
                        { n: "02", icon: Zap, title: "Drop the idea", body: "Half a sentence works. \"Lesson from my first failed hire.\" Lakshmi handles strategy, writing, and editing." },
                        { n: "03", icon: Globe, title: "Post everywhere", body: "Get polished drafts for all four platforms. Copy, schedule, or tweak — your call." },
                    ].map((s, i) => (
                        <motion.div
                            key={s.n}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.12 }}
                            viewport={{ once: true }}
                            className="lk-border lk-surface lk-card-hover rounded-2xl p-7 flex flex-col"
                        >
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
                                style={{ background: "rgba(124,58,237,0.15)", border: "1px solid rgba(124,58,237,0.25)" }}>
                                <s.icon className="w-5 h-5 text-[var(--lk-primary)]" />
                            </div>
                            <div className="font-mono text-xs text-[var(--lk-primary)] mb-2">{s.n}</div>
                            <div className="font-heading text-xl mb-2">{s.title}</div>
                            <div className="text-sm text-[var(--lk-muted)] leading-relaxed">{s.body}</div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ── Agents ── */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 border-t border-[var(--lk-border)]">
                <div className="grid md:grid-cols-12 gap-6 mb-12">
                    <div className="md:col-span-5">
                        <div className="lk-label mb-3">How the AI LinkedIn post writer works</div>
                        <h2 className="font-heading text-3xl sm:text-5xl tracking-tight">Lakshmi's 4-agent pipeline</h2>
                    </div>
                    <p className="md:col-span-6 md:col-start-7 text-[var(--lk-muted)] leading-relaxed self-end text-sm sm:text-base">
                        Unlike generic AI writing tools, Lakshmi runs four specialist agents in sequence — strategist, voice coach, ghostwriter, QA editor. That's why the output doesn't sound like ChatGPT.
                    </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {AGENTS.map((a, i) => (
                        <motion.div
                            key={a.title}
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.1 }}
                            viewport={{ once: true }}
                            className="lk-border lk-surface lk-card-hover p-6 rounded-2xl flex flex-col"
                        >
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                                style={{ background: `${a.color}18`, border: `1px solid ${a.color}30` }}>
                                <a.icon className="w-5 h-5" style={{ color: a.color }} />
                            </div>
                            <div className="font-semibold text-lg mb-2">{a.title}</div>
                            <div className="text-sm text-[var(--lk-muted)] leading-relaxed flex-1">{a.body}</div>
                            <div className="mt-4 inline-flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: a.color }} />
                                <span className="text-xs font-medium" style={{ color: a.color }}>{a.tag}</span>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ── Before / After ── */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 border-t border-[var(--lk-border)]">
                <div className="text-center mb-14">
                    <div className="lk-label mb-3">The honest comparison</div>
                    <h2 className="font-heading text-3xl sm:text-5xl tracking-tight">
                        Generic AI gives you noise.{" "}
                        <span className="lk-gradient-text">Lakshmi gives you signal.</span>
                    </h2>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                    <div className="lk-border lk-surface rounded-2xl p-6 sm:p-8">
                        <div className="flex items-center gap-2 mb-6">
                            <div className="w-2 h-2 rounded-full bg-red-400" />
                            <span className="lk-label">Generic AI output</span>
                        </div>
                        <p className="text-[var(--lk-muted)] italic leading-relaxed text-sm">
                            "In today's fast-paced world, artificial intelligence is transforming how we do business. As a thought leader in this space, I've learned that embracing AI is not just an option but a necessity..."
                        </p>
                        <p className="text-[var(--lk-muted)] italic text-sm mt-3">
                            "What are your thoughts? Let me know in the comments! #AI #ThoughtLeadership #Innovation"
                        </p>
                        <div className="mt-6 pt-4 border-t border-[var(--lk-border)] text-xs text-[var(--lk-muted)]">
                            No hook. No story. No voice. Sounds like 10,000 other posts.
                        </div>
                    </div>
                    <div className="rounded-2xl p-6 sm:p-8 relative overflow-hidden"
                        style={{ border: "1px solid rgba(124,58,237,0.35)", background: "rgba(124,58,237,0.05)" }}>
                        <div className="absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl pointer-events-none"
                            style={{ background: "rgba(124,58,237,0.12)" }} />
                        <div className="flex items-center gap-2 mb-6">
                            <div className="w-2 h-2 rounded-full bg-emerald-400" />
                            <span className="lk-label text-[var(--lk-primary)]">Lakshmi output</span>
                        </div>
                        <div className="relative space-y-3">
                            <p className="font-bold text-xl sm:text-2xl text-[var(--lk-primary)] leading-snug">
                                "I used to think AI was about better answers. Now I know it's about better delegation."
                            </p>
                            <p className="text-sm leading-relaxed text-[var(--lk-text)]">
                                The shift happened when I stopped writing prompts and started wiring loops.{"\n\n"}
                                An agent isn't a chatbot. It's perception, reasoning, action — on repeat.{"\n\n"}
                                The best ones don't try to be smarter than you. They save you the boring 80%.
                            </p>
                            <p className="italic text-sm text-[var(--lk-muted)]">What's the first task you'd hand off to an agent?</p>
                            <p className="font-mono text-xs text-[var(--lk-primary)]">#AI #Agents #BuildInPublic</p>
                        </div>
                        <div className="mt-6 pt-4 border-t border-[rgba(124,58,237,0.2)] text-xs text-[var(--lk-muted)]">
                            Strong hook. Personal story. Clear CTA. Tight hashtags. Unmistakably human.
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Testimonials ── */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 border-t border-[var(--lk-border)]">
                <div className="text-center mb-14">
                    <div className="lk-label mb-3">What creators are saying</div>
                    <h2 className="font-heading text-3xl sm:text-5xl tracking-tight">Real people. Real results.</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {TESTIMONIALS.map((t, i) => (
                        <motion.div
                            key={t.name}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.1 }}
                            viewport={{ once: true }}
                            className="lk-border lk-surface lk-card-hover rounded-2xl p-6 flex flex-col"
                        >
                            <div className="flex items-center gap-1 mb-4">
                                {[...Array(t.stars)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />)}
                            </div>
                            <p className="text-sm text-[var(--lk-muted)] leading-relaxed flex-1">"{t.text}"</p>
                            <div className="flex items-center gap-3 mt-5 pt-4 border-t border-[var(--lk-border)]">
                                <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                                    style={{ backgroundColor: t.avatarBg }}>
                                    {t.avatar}
                                </div>
                                <div>
                                    <div className="text-sm font-semibold text-[var(--lk-text)]">{t.name}</div>
                                    <div className="text-xs text-[var(--lk-muted)]">{t.role}</div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ── Stats bar ── */}
            <section className="border-y border-[var(--lk-border)] bg-[var(--lk-surface)]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
                        {[
                            { val: "200+", label: "Active creators" },
                            { val: "4", label: "Platforms supported" },
                            { val: "< 60s", label: "Time to first draft" },
                            { val: "3×", label: "Avg. profile growth" },
                        ].map(s => (
                            <div key={s.val}>
                                <div className="font-heading text-3xl sm:text-4xl lk-gradient-text">{s.val}</div>
                                <div className="text-xs text-[var(--lk-muted)] mt-1">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── FAQ ── */}
            <section className="max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-28 border-t border-[var(--lk-border)]">
                <div className="text-center mb-12">
                    <div className="lk-label mb-3">Got questions?</div>
                    <h2 className="font-heading text-3xl sm:text-5xl tracking-tight">Frequently asked</h2>
                </div>
                <div className="space-y-3">
                    {SCHEMA_FAQ.mainEntity.map((item, i) => (
                        <FaqItem key={i} q={item.name} a={item.acceptedAnswer.text} />
                    ))}
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
                <div className="relative rounded-3xl overflow-hidden"
                    style={{ background: "linear-gradient(135deg, #13162B 0%, #0D0F1A 100%)", border: "1px solid rgba(124,58,237,0.3)" }}>
                    {/* Glow blobs */}
                    <div className="absolute top-0 left-1/4 w-64 h-64 rounded-full blur-3xl pointer-events-none"
                        style={{ background: "rgba(124,58,237,0.12)" }} />
                    <div className="absolute bottom-0 right-1/4 w-48 h-48 rounded-full blur-3xl pointer-events-none"
                        style={{ background: "rgba(6,182,212,0.08)" }} />

                    <div className="grid md:grid-cols-12 items-stretch relative z-10">
                        <div className="md:col-span-8 p-8 sm:p-12 md:p-16">
                            <div className="lk-label mb-4">Your next post is one click away</div>
                            <h3 className="font-heading text-3xl sm:text-5xl">Stop staring at the blank box.</h3>
                            <p className="mt-4 text-[var(--lk-muted)] text-sm sm:text-base max-w-md">
                                Sign up in ten seconds. Teach Lakshmi your voice once. Never write from scratch again.
                            </p>
                            <Link to="/signup"
                                className="lk-btn-primary rounded-xl px-7 py-4 mt-8 inline-flex items-center gap-2 text-sm"
                                data-testid="cta-signup-btn">
                                Get my free account <ArrowRight className="w-4 h-4" />
                            </Link>
                            <div className="mt-4 text-xs text-[var(--lk-muted)]">No credit card. No "book a demo". Just start writing.</div>
                        </div>
                        <div className="md:col-span-4 flex items-end justify-center relative overflow-hidden min-h-[180px]">
                            <picture>
                                <source srcSet="/image/lakshmi_nobg.webp" type="image/webp" />
                                <img
                                    src="/image/lakshmi_nobg_opt.png"
                                    alt=""
                                    aria-hidden="true"
                                    width="560"
                                    height="1120"
                                    loading="lazy"
                                    className="h-56 sm:h-64 md:h-full w-auto object-contain object-bottom"
                                    style={{
                                        mixBlendMode: "screen",
                                        maskImage: "linear-gradient(to top, black 65%, transparent 100%)",
                                        WebkitMaskImage: "linear-gradient(to top, black 65%, transparent 100%)",
                                        filter: "drop-shadow(0 0 24px rgba(124,58,237,0.4))",
                                    }}
                                />
                            </picture>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Footer ── */}
            <footer className="border-t border-[var(--lk-border)]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                    <div>
                        <div className="font-semibold text-lg flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-[var(--lk-primary)]" /> Lakshmi
                        </div>
                        <div className="text-xs text-[var(--lk-muted)] mt-1">Your AI social media buddy.</div>
                    </div>
                    <nav className="flex flex-wrap gap-5 text-xs text-[var(--lk-muted)]">
                        <Link to="/features" className="hover:text-[var(--lk-text)] transition-colors">Features</Link>
                        <Link to="/pricing" className="hover:text-[var(--lk-text)] transition-colors">Pricing</Link>
                        <Link to="/about" className="hover:text-[var(--lk-text)] transition-colors">About</Link>
                        <Link to="/use-cases/linkedin" className="hover:text-[var(--lk-text)] transition-colors">LinkedIn</Link>
                        <Link to="/use-cases/personal-branding" className="hover:text-[var(--lk-text)] transition-colors">Personal Branding</Link>
                        <Link to="/signup" className="hover:text-[var(--lk-text)] transition-colors">Sign up free</Link>
                        <Link to="/login" className="hover:text-[var(--lk-text)] transition-colors">Log in</Link>
                    </nav>
                    <div className="font-mono text-xs text-[var(--lk-muted)]">© 2026 Lakshmi</div>
                </div>
            </footer>
        </div>
    );
}
